"use client"

import * as React from "react"
import { startTransition } from "react"
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  ColumnDef,
  ColumnFiltersState,
  type HeaderContext,
  PaginationState,
  Row,
  type Cell,
  SortingState,
  Updater,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ChevronDown, ChevronsDownIcon, ChevronsUpIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { SortableRowHandleProvider } from "@/components/ui/data-table-sortable-context"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { DataTablePagination } from "@/components/ui/data-table-pagination"
import { getColumnSortDirection } from "@/components/ui/data-table-sortable-header"
import {
  DataTableRowActions,
  type DataTableSelectionAction,
} from "@/components/ui/data-table-row-actions"
import {
  DATA_TABLE_MIN_WIDTH_PX,
  buildDataTablePinMap,
  getColumnMeta as getDataTableColumnMeta,
  resolveColStyle,
  sumColumnMinWidths,
  type DataTableColumnMeta,
  type DataTablePinInfo,
} from "@/components/ui/data-table-column-width"
import {
  getAdminTablePageSerial,
  getAdminTableStableSerial,
  type AdminTableSerialColumnMeta,
  type AdminTableSerialOrder,
} from "@/components/admin/admin-table-utils"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  filterColumn?: string
  filterPlaceholder?: string
  showColumnToggle?: boolean
  showPagination?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  showPageSizeSelector?: boolean
  showPageJumpInput?: boolean

  defaultPageSize?: number

  defaultSorting?: SortingState
  getRowId?: (originalRow: TData, index: number) => string
  selectionActionLabel?: string
  selectionActionLoading?: boolean
  onSelectionAction?: (rows: TData[]) => Promise<void> | void

  selectionActions?: DataTableSelectionAction<TData>[]
  onSelectedRowsChange?: (rows: TData[]) => void

  maxSelectedRows?: number
  selectionResetToken?: number
  onReorder?: (rows: TData[]) => void | Promise<void>
  paginationResetKey?: unknown
  serialOrder?: AdminTableSerialOrder

  manualPagination?: boolean

  manualSorting?: boolean
  sorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void

  isPending?: boolean

  emptyState?: React.ReactNode

  padEmptyRows?: boolean

  forceFixedBodyHeight?: boolean

  fixedBodyRows?: number
  rowCount?: number
  pagination?: PaginationState
  onPaginationChange?: (pagination: PaginationState) => void

  tableMinWidth?: number

  density?: DataTableDensity

  frameless?: boolean
}

const DATA_TABLE_DENSITY = {

  default: { rowHeight: "3rem", headHeight: "2.5rem", tableClassName: "" },

  compact: {
    rowHeight: "2.5rem",
    headHeight: "2rem",

    tableClassName: "text-xs [&_td]:py-1",
  },
} as const

export type DataTableDensity = keyof typeof DATA_TABLE_DENSITY

const ROW_HEIGHT_VAR = "--data-table-row-height"
const HEAD_HEIGHT_VAR = "--data-table-head-height"
const ROW_HEIGHT_CLASS = "h-[var(--data-table-row-height)]"

const SCROLL_JUMP_SIZE = "1.75rem"

function EmptyPadRows({
  count,
  colSpan,
}: {
  count: number
  colSpan: number
}) {
  if (count <= 0) {
    return null
  }

  return Array.from({ length: count }).map((_, index) => (
    <TableRow
      key={`empty-pad-${index}`}
      className={cn(ROW_HEIGHT_CLASS, "hover:bg-transparent")}
    >
      {/* 占位高度由行高变量决定，内层定高再叠单元格留白/边框会把空行撑高。 */}
      <TableCell colSpan={colSpan} className="px-3" />
    </TableRow>
  ))
}
function readColumnMeta(meta: unknown): DataTableColumnMeta | undefined {
  return getDataTableColumnMeta(meta)
}

function getDataTableCellClassName(meta?: DataTableColumnMeta) {
  return cn(meta?.className, meta?.align === "center" && "text-center")
}

function DataTableCellContent({
  noTruncate = false,
  children,
}: {
  noTruncate?: boolean
  children: React.ReactNode
}) {
  if (noTruncate) {
    return children
  }

  return <div className="min-w-0 truncate">{children}</div>
}

function isSerialColumn<TData>(column: ColumnDef<TData, unknown>) {
  return (
    column.id === "serial" ||
    String((column as { accessorKey?: unknown }).accessorKey ?? "") === "serial"
  )
}

function getCellLayoutProps(meta?: DataTableColumnMeta) {
  return {
    className: getDataTableCellClassName(meta),
    style: resolveColStyle(meta?.colWidth),
  }
}

function applyPinLayout(
  layout: { className?: string; style?: React.CSSProperties },
  pin: DataTablePinInfo | undefined,
  variant: "head" | "cell",
  stickyMode: boolean,
): { className?: string; style?: React.CSSProperties } {
  if (!stickyMode) {
    return layout
  }

  const isHead = variant === "head"
  const className = cn(
    layout.className,
    isHead && "data-table-sticky-head-cell",
    pin && "sticky",
    pin && !isHead && "data-table-pin-cell",
    pin?.side === "left" && pin.edge && "data-table-pin-left-edge",
    pin?.side === "right" && pin.edge && "data-table-pin-right-edge",
  )

  const style: React.CSSProperties = { ...layout.style }
  if (pin) {
    if (pin.side === "left") {
      style.left = pin.offset
    } else {
      style.right = pin.offset
    }
    style.zIndex = isHead ? 30 : 10
  }

  return { className, style }
}

function DataTableRowCheckbox({
  checked,
  disabled,
  onToggle,
}: {
  checked: boolean
  disabled: boolean
  onToggle: (checked: boolean) => void
}) {
  const t = useTranslations("site.ui")

  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onToggle(!!value)}
      aria-label={t("selectRow")}
    />
  )
}

function renderDataTableBodyCell<TData>(
  row: Row<TData>,
  cell: Cell<TData, unknown>,
  isSelected: boolean,
  canSelect: boolean,
  options?: {
    serialOrder?: AdminTableSerialOrder
    rowsOnPage: number
    coreRowIndexById?: Map<string, number>
  },
  pinMap?: Map<string, DataTablePinInfo>,
  stickyMode = false,
) {
  const meta = readColumnMeta(cell.column.columnDef.meta)
  const layout = applyPinLayout(
    getCellLayoutProps(meta),
    pinMap?.get(cell.column.id),
    "cell",
    stickyMode,
  )

  if (cell.column.id === "select") {
    return (
      <TableCell key={cell.id} {...layout}>
        <DataTableRowCheckbox
          checked={isSelected}
          disabled={!canSelect}
          onToggle={(value) => row.toggleSelected(value)}
        />
      </TableCell>
    )
  }

  if (
    options?.serialOrder &&
    isSerialColumn(cell.column) &&
    !(cell.column.columnDef.meta as { customSerial?: boolean } | undefined)
      ?.customSerial
  ) {
    const columnMeta = cell.column.columnDef.meta as
      | AdminTableSerialColumnMeta
      | undefined;
    const serialStrategy =
      columnMeta?.serialStrategy ??
      (columnMeta?.stableSerial ? "auto-stable" : "auto-page");
    const coreIndex =
      options.coreRowIndexById?.get(row.id) ?? row.index;

    return (
      <TableCell key={cell.id} {...layout}>
        <DataTableCellContent noTruncate={meta?.noTruncate}>
          {serialStrategy === "auto-stable"
            ? getAdminTableStableSerial(coreIndex)
            : getAdminTablePageSerial(
                row.index,
                options.rowsOnPage,
                options.serialOrder,
              )}
        </DataTableCellContent>
      </TableCell>
    )
  }

  return (
    <TableCell key={cell.id} {...layout}>
      <DataTableCellContent noTruncate={meta?.noTruncate}>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </DataTableCellContent>
    </TableCell>
  )
}

export function DataTableSelectAllCheckbox({
  checked,
  indeterminate = false,
  disabled = false,
  onToggle,
}: {
  checked: boolean
  indeterminate?: boolean
  disabled?: boolean
  onToggle: (checked: boolean) => void
}) {
  const t = useTranslations("site.ui")

  return (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      onCheckedChange={(value) => onToggle(!!value)}
      aria-label={t("selectAll")}
    />
  )
}

function renderDataTableHeaderCell<TData>(
  header: {
    id: string
    isPlaceholder: boolean
    column: { id: string; columnDef: ColumnDef<TData, unknown> }
    getContext: () => unknown
  },
  allPageRowsSelected: boolean,
  somePageRowsSelected: boolean,
  selectAllDisabled: boolean,
  onToggleAllPageRows: (checked: boolean) => void,
  _sorting: SortingState,
) {
  if (header.isPlaceholder) {
    return null
  }

  if (header.column.id === "select") {
    return (
      <DataTableSelectAllCheckbox
        checked={allPageRowsSelected}
        indeterminate={somePageRowsSelected && !allPageRowsSelected}
        disabled={selectAllDisabled}
        onToggle={onToggleAllPageRows}
      />
    )
  }

  return flexRender(
    header.column.columnDef.header,
    header.getContext() as HeaderContext<TData, unknown>,
  )
}

function DataTableScrollJump({
  viewportRef,
}: {
  viewportRef: React.RefObject<HTMLDivElement | null>
}) {
  const t = useTranslations("site.ui")
  const [state, setState] = React.useState({
    scrollable: false,
    atTop: true,
    bottomInset: 0,
    rightInset: 0,
  })

  React.useEffect(() => {
    const viewport = viewportRef.current

    if (!viewport) {
      return
    }

    const sync = () => {
      const scrollable = viewport.scrollHeight - viewport.clientHeight > 1
      const atTop = viewport.scrollTop <= 1

      const bottomInset =
        viewport.offsetHeight - viewport.clientHeight - viewport.clientTop
      const rightInset =
        viewport.offsetWidth - viewport.clientWidth - viewport.clientLeft

      setState((current) =>
        current.scrollable === scrollable &&
        current.atTop === atTop &&
        current.bottomInset === bottomInset &&
        current.rightInset === rightInset
          ? current
          : { scrollable, atTop, bottomInset, rightInset },
      )
    }

    sync()
    viewport.addEventListener("scroll", sync, { passive: true })

    const observer = new ResizeObserver(sync)
    observer.observe(viewport)
    if (viewport.firstElementChild) {
      observer.observe(viewport.firstElementChild)
    }

    return () => {
      viewport.removeEventListener("scroll", sync)
      observer.disconnect()
    }
  }, [viewportRef])

  if (!state.scrollable) {
    return null
  }

  const JumpIcon = state.atTop ? ChevronsDownIcon : ChevronsUpIcon

  return (
    <button
      type="button"
      aria-label={state.atTop ? t("scrollToBottom") : t("scrollToTop")}
      className="absolute z-40 inline-flex size-7 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-60 shadow-sm transition hover:text-foreground hover:opacity-100"
      style={{

        bottom: `calc(${state.bottomInset}px + var(--data-table-row-height) - ${SCROLL_JUMP_SIZE} / 2)`,
        right: `calc(${state.rightInset}px + 0.5rem)`,
      }}
      onClick={() => {
        const viewport = viewportRef.current

        if (!viewport) {
          return
        }

        viewport.scrollTo({
          top: state.atTop ? viewport.scrollHeight : 0,
          behavior: "smooth",
        })
      }}
    >
      <JumpIcon className="size-4" />
    </button>
  )
}

function countSelectedRows(rowSelection: Record<string, boolean>) {
  let count = 0

  for (const id in rowSelection) {
    if (rowSelection[id]) count += 1
  }

  return count
}

function buildTableLayoutStyle(minWidth: number): React.CSSProperties {
  return {
    width: "100%",
    minWidth,
    ["--data-table-min-width" as string]: `${minWidth}px`,
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  filterColumn,
  filterPlaceholder = "\u641c\u7d22...",
  showColumnToggle = false,
  showPagination = true,
  pageSize = 10,
  pageSizeOptions,
  showPageSizeSelector = false,

  showPageJumpInput = true,
  defaultPageSize,
  defaultSorting,
  getRowId,
  selectionActionLabel,
  selectionActionLoading = false,
  onSelectionAction,
  selectionActions,
  onSelectedRowsChange,
  maxSelectedRows,
  selectionResetToken,
  onReorder,
  paginationResetKey,
  serialOrder,
  manualPagination = false,
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange: onControlledSortingChange,
  isPending = false,
  emptyState,
  padEmptyRows = true,
  forceFixedBodyHeight = false,
  fixedBodyRows,
  rowCount,
  pagination: controlledPagination,
  onPaginationChange: onControlledPaginationChange,
  tableMinWidth,

  density = "compact",
  frameless = false,
}: DataTableProps<TData, TValue>) {
  const densityConfig = DATA_TABLE_DENSITY[density]
  const [sorting, setSorting] = React.useState<SortingState>(
    () => controlledSorting ?? defaultSorting ?? [],
  )
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({})
  const [activeDragId, setActiveDragId] = React.useState<string | null>(null)
  const [activeDragLayout, setActiveDragLayout] = React.useState<{
    width: number
    height: number
    cellWidths: number[]
  } | null>(null)

  const selectedRowCount = React.useMemo(
    () => countSelectedRows(rowSelection),
    [rowSelection],
  )
  const [internalPagination, setInternalPagination] = React.useState({
    pageIndex: 0,
    pageSize,
  })
  const pagination = controlledPagination ?? internalPagination
  const resolvedSorting = controlledSorting ?? sorting
  const paginationRef = React.useRef(pagination)
  paginationRef.current = pagination

  const handlePaginationChange = React.useCallback(
    (updater: Updater<PaginationState>) => {
      const next =
        typeof updater === "function"
          ? updater(paginationRef.current)
          : updater

      if (onControlledPaginationChange) {
        onControlledPaginationChange(next)
        return
      }

      setInternalPagination(next)
    },
    [onControlledPaginationChange],
  )

  const handleSortingChange = React.useCallback(
    (updater: Updater<SortingState>) => {
      const next =
        typeof updater === "function" ? updater(resolvedSorting) : updater

      if (onControlledSortingChange) {
        onControlledSortingChange(next)
      } else {
        setSorting(next)
      }

      if (!onControlledSortingChange) {
        handlePaginationChange((current) =>
          current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
        )
      }
    },
    [handlePaginationChange, onControlledSortingChange, resolvedSorting],
  )

  const lastRowCountRef = React.useRef(0)

  if (!isPending) {
    lastRowCountRef.current = rowCount ?? data.length
  }

  const resolvedRowCount = isPending
    ? lastRowCountRef.current
    : (rowCount ?? data.length)
  const resolvedPageCount = Math.max(
    Math.ceil(resolvedRowCount / Math.max(pagination.pageSize, 1)),
    1,
  )

  const table = useReactTable({
    data,
    columns,
    getRowId,
    enableSortingRemoval: false,
    enableRowSelection:
      maxSelectedRows == null
        ? true
        : (row) => row.getIsSelected() || selectedRowCount < maxSelectedRows,
    getCoreRowModel: getCoreRowModel(),
    ...(showPagination && !manualPagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: resolvedPageCount,
        }
      : {}),
    ...(manualSorting
      ? { manualSorting: true }
      : { getSortedRowModel: getSortedRowModel() }),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting: resolvedSorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  })

  const pinMap = React.useMemo(
    () =>
      buildDataTablePinMap(
        table.getVisibleLeafColumns().map((column) => ({
          id: column.id,
          meta: column.columnDef.meta,
        })),
      ),
    [table, columnVisibility, columns],
  )

  const stickyMode = true

  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  )

  const currentPageRows = table.getRowModel().rows
  const sortableRowIds = onReorder
    ? currentPageRows.map((row) => row.id)
    : []
  const activeDragRow = activeDragId
    ? currentPageRows.find((row) => row.id === activeDragId)
    : null
  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original)
  const selectedCount = selectedRows.length
  const canSelectMoreRows =
    maxSelectedRows == null || selectedRowCount < maxSelectedRows

  const selectedOnPageCount = currentPageRows.reduce(
    (count, row) => (rowSelection[row.id] ? count + 1 : count),
    0,
  )
  const allPageRowsSelected =
    currentPageRows.length > 0 && selectedOnPageCount === currentPageRows.length
  const somePageRowsSelected = selectedOnPageCount > 0

  const selectAllDisabled = !allPageRowsSelected && !canSelectMoreRows
  const pageCount = manualPagination
    ? resolvedPageCount
    : Math.max(table.getPageCount(), 1)
  const filteredTotal = manualPagination
    ? resolvedRowCount
    : table.getFilteredRowModel().rows.length
  const resolvedDefaultPageSize =
    defaultPageSize ?? (showPagination ? pageSize : undefined)
  const paginationVisibilityRows = fixedBodyRows ?? resolvedDefaultPageSize
  const showPaginationControls =
    showPagination &&
    (filteredTotal > pagination.pageSize ||

      (showPageSizeSelector && paginationVisibilityRows != null && filteredTotal > paginationVisibilityRows))

  const showFooterRow =
    showPagination && (showPaginationControls || selectedCount > 0)
  const fixedBodyRowCount =
    fixedBodyRows ??
    (forceFixedBodyHeight ? pagination.pageSize : resolvedDefaultPageSize)

  const fixedHeightActive =
    fixedBodyRowCount != null &&
    (fixedBodyRows != null ||
      (showPagination &&
        (forceFixedBodyHeight || filteredTotal > fixedBodyRowCount)))
  const padTargetRows =
    fixedBodyRows ??
    (forceFixedBodyHeight || showPaginationControls ? pagination.pageSize : 0)
  const emptyPadCount =
    padEmptyRows &&
    !(isPending && currentPageRows.length === 0) &&
    padTargetRows > 0 &&
    (forceFixedBodyHeight || currentPageRows.length > 0) &&
    currentPageRows.length < padTargetRows
      ? Math.max(0, padTargetRows - currentPageRows.length - (currentPageRows.length === 0 && emptyState != null ? 1 : 0))
      : 0
  const resolvedTableMinWidth = Math.max(
    tableMinWidth ?? DATA_TABLE_MIN_WIDTH_PX,
    sumColumnMinWidths(columns),
  )
  const tableLayoutStyle = buildTableLayoutStyle(resolvedTableMinWidth)
  const stickyWrapperRef = React.useRef<HTMLDivElement>(null)

  const [stickyScrollbarPad, setStickyScrollbarPad] = React.useState(0)
  const [measuredViewportHeight, setMeasuredViewportHeight] = React.useState<number | null>(null)
  const stickyFixedHeight =
    fixedBodyRowCount != null
      ? measuredViewportHeight != null
        ? `${measuredViewportHeight + stickyScrollbarPad}px`
        : `calc(${densityConfig.headHeight} + ${fixedBodyRowCount} * ${densityConfig.rowHeight} + ${stickyScrollbarPad}px)`
      : undefined
  const stickyWrapperStyle: React.CSSProperties | undefined = stickyFixedHeight
    ? {
        maxHeight: stickyFixedHeight,

        ...(fixedHeightActive ? { minHeight: stickyFixedHeight } : {}),
      }
    : undefined
  React.useLayoutEffect(() => {
    const el = stickyWrapperRef.current
    if (!el) {
      return
    }
    const renderedTable = el.querySelector("table")
    const visibleRows = fixedBodyRowCount == null
      ? []
      : Array.from(renderedTable?.tBodies[0]?.rows ?? []).slice(0, fixedBodyRowCount)

    const measure = () => {
      const style = getComputedStyle(el)
      const borderY =
        (parseFloat(style.borderTopWidth) || 0) +
        (parseFloat(style.borderBottomWidth) || 0)

      const gap = Math.round(el.offsetHeight - el.clientHeight)
      const next = Math.max(gap, Math.round(borderY))
      setStickyScrollbarPad((prev) => (prev === next ? prev : next))

      if (!isPending && currentPageRows.length > 0 && fixedBodyRowCount != null && renderedTable?.tHead) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        const minimumRowHeight = parseFloat(densityConfig.rowHeight) * rootFontSize
        const rowsHeight = visibleRows.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0)
        const missingRowsHeight = Math.max(0, fixedBodyRowCount - visibleRows.length) * minimumRowHeight
        const height = Math.ceil(renderedTable.tHead.getBoundingClientRect().height + rowsHeight + missingRowsHeight)
        setMeasuredViewportHeight((prev) => prev === height ? prev : height)
      }
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)

    if (renderedTable) observer.observe(renderedTable)
    if (renderedTable?.tHead) observer.observe(renderedTable.tHead)
    for (const row of visibleRows) observer.observe(row)

    return () => observer.disconnect()
  }, [
    resolvedTableMinWidth,
    currentPageRows,
    fixedBodyRowCount,
    emptyPadCount,
    densityConfig,
    pagination.pageSize,
    isPending,
  ])

  const tableWrapperClass = cn(
    "w-full overflow-auto",
    frameless
      ? showFooterRow && "border-b"
      : "rounded-md border",
  )

  const tableClass = cn(
    "w-full table-fixed [&_th]:h-[var(--data-table-head-height)]",
    densityConfig.tableClassName,
  )
  const handleToggleAllPageRows = React.useCallback(
    (checked: boolean) => {
      if (!checked) {
        table.toggleAllPageRowsSelected(false)
        return
      }

      if (maxSelectedRows == null) {
        table.toggleAllPageRowsSelected(true)
        return
      }

      const pageRowIds = table.getRowModel().rows.map((row) => row.id)

      setRowSelection((current) => {
        const remaining = maxSelectedRows - countSelectedRows(current)

        if (remaining <= 0) {
          return current
        }

        const next = { ...current }
        let added = 0

        for (const rowId of pageRowIds) {
          if (added >= remaining) {
            break
          }

          if (next[rowId]) {
            continue
          }

          next[rowId] = true
          added += 1
        }

        return added > 0 ? next : current
      })
    },
    [maxSelectedRows, table],
  )

  const selectionResetRef = React.useRef(selectionResetToken)
  const defaultSortingRef = React.useRef(defaultSorting)
  const handlePaginationChangeRef = React.useRef(handlePaginationChange)
  const onControlledSortingChangeRef = React.useRef(onControlledSortingChange)
  const onSelectedRowsChangeRef = React.useRef(onSelectedRowsChange)
  const dataRef = React.useRef(data)
  const getRowIdRef = React.useRef(getRowId)
  const paginationResetKeyRef = React.useRef(paginationResetKey)

  onSelectedRowsChangeRef.current = onSelectedRowsChange
  dataRef.current = data
  getRowIdRef.current = getRowId
  defaultSortingRef.current = defaultSorting
  handlePaginationChangeRef.current = handlePaginationChange
  onControlledSortingChangeRef.current = onControlledSortingChange

  React.useEffect(() => {
    const notifySelectionChange = onSelectedRowsChangeRef.current

    if (!notifySelectionChange) {
      return
    }

    startTransition(() => {
      const selectedIds = new Set(
        Object.keys(rowSelection).filter((id) => rowSelection[id]),
      )
      const selected = dataRef.current.filter((row, index) => {
        const rowId = getRowIdRef.current?.(row, index) ?? String(index)
        return selectedIds.has(rowId)
      })
      notifySelectionChange(selected)
    })
  }, [rowSelection])

  React.useEffect(() => {
    if (
      selectionResetToken !== undefined &&
      selectionResetRef.current !== selectionResetToken
    ) {
      selectionResetRef.current = selectionResetToken
      setRowSelection({})
    }
  }, [selectionResetToken])

  React.useEffect(() => {
    if (isPending) {
      return
    }

    const maxPageIndex = Math.max(pageCount - 1, 0)
    if (pagination.pageIndex > maxPageIndex) {
      handlePaginationChangeRef.current((current) => ({
        ...current,
        pageIndex: maxPageIndex,
      }))
    }
  }, [isPending, pageCount, pagination.pageIndex])

  React.useEffect(() => {
    if (paginationResetKey === undefined) {
      return
    }

    if (paginationResetKeyRef.current === paginationResetKey) {
      return
    }

    paginationResetKeyRef.current = paginationResetKey

    startTransition(() => {
      handlePaginationChangeRef.current((current) =>
        current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
      )
      setRowSelection({})
      const nextSorting = defaultSortingRef.current ?? []
      if (onControlledSortingChangeRef.current) {
        onControlledSortingChangeRef.current(nextSorting)
      } else {
        setSorting(nextSorting)
      }
    })
  }, [paginationResetKey])

  const serialColumnSort = resolvedSorting.find((entry) => entry.id === "serial")
  const effectiveSerialOrder: AdminTableSerialOrder =
    serialColumnSort != null
      ? serialColumnSort.desc
        ? "desc"
        : "asc"
      : (serialOrder ?? "desc")

  const coreRowIndexById = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const coreRow of table.getCoreRowModel().rows) {
      map.set(coreRow.id, coreRow.index)
    }
    return map
  }, [table, data])

  const bodyCellOptions = serialOrder
    ? {
        serialOrder: effectiveSerialOrder,
        rowsOnPage: currentPageRows.length,
        coreRowIndexById,
      }
    : undefined

  function handleSortableDragStart(event: DragStartEvent) {
    const id = String(event.active.id)
    const element = Array.from(
      stickyWrapperRef.current?.querySelectorAll<HTMLTableRowElement>(
        "tbody > tr[data-sortable-row-id]",
      ) ?? [],
    ).find(row => row.dataset.sortableRowId === id)

    setActiveDragId(id)
    if (element) {
      const rect = element.getBoundingClientRect()
      setActiveDragLayout({
        width: rect.width,
        height: rect.height,
        cellWidths: Array.from(element.cells, cell => cell.getBoundingClientRect().width),
      })
    } else {
      setActiveDragLayout(null)
    }
  }

  function clearSortableDrag() {
    setActiveDragId(null)
    setActiveDragLayout(null)
  }

  function handleSortableDragEnd(event: DragEndEvent) {
    if (!onReorder) {
      return
    }

    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const activeIndex = sortableRowIds.indexOf(String(active.id))
    const overIndex = sortableRowIds.indexOf(String(over.id))

    if (activeIndex < 0 || overIndex < 0) {
      return
    }

    const reorderedRows = arrayMove(
      currentPageRows.map((row) => row.original),
      activeIndex,
      overIndex,
    )

    void onReorder(reorderedRows)
  }

  const tableBodyContent =
    currentPageRows.length ? (
      currentPageRows.map((row) => {
        if (onReorder) {
          const isSelected = row.getIsSelected()
          const rowCells = row
            .getVisibleCells()
            .map((cell) =>
              renderDataTableBodyCell(
                row,
                cell,
                isSelected,
                isSelected || canSelectMoreRows,
                bodyCellOptions,
                pinMap,
                stickyMode,
              ),
            )

          return (
            <SortableDataTableRow
              key={row.id}
              id={row.id}
              className={ROW_HEIGHT_CLASS}
            >
              {rowCells}
            </SortableDataTableRow>
          )
        }

        const isSelected = row.getIsSelected()

        return (
          <MemoizedDataTableRow
            key={row.id}
            row={row}
            isSelected={isSelected}
            canSelect={isSelected || canSelectMoreRows}
            bodyCellOptions={bodyCellOptions}
            pinMap={pinMap}
            stickyMode={stickyMode}
          />
        )
      })
    ) : isPending ? (
      Array.from({ length: Math.min(pagination.pageSize, fixedBodyRowCount ?? pagination.pageSize) }, (_, index) => (
        <TableRow key={`loading-${index}`} className={ROW_HEIGHT_CLASS} aria-hidden="true">
          {table.getVisibleLeafColumns().map(column => <TableCell key={column.id}><Skeleton className="h-4 w-3/4" /></TableCell>)}
        </TableRow>
      ))
    ) : forceFixedBodyHeight && emptyState == null ? null : (
      <TableRow>
        <TableCell colSpan={columns.length} className="h-24 text-center">
          {emptyState ?? "\u6682\u65e0\u6570\u636e"}
        </TableCell>
      </TableRow>
    )

  return (
    <div
      className="w-full"
      aria-busy={isPending}
      style={
        {
          [ROW_HEIGHT_VAR]: densityConfig.rowHeight,
          [HEAD_HEIGHT_VAR]: densityConfig.headHeight,
        } as React.CSSProperties
      }
    >
      {(filterColumn || showColumnToggle) && (
        <div className="flex items-center py-4 gap-2">
          {filterColumn && (
            <Input
              placeholder={filterPlaceholder}
              value={(table.getColumn(filterColumn)?.getFilterValue() as string) ?? ""}
              onChange={(event) =>
                table.getColumn(filterColumn)?.setFilterValue(event.target.value)
              }
              className="max-w-sm"
            />
          )}
          {showColumnToggle && (
            <DropdownMenu>
              <DropdownMenuTrigger className="ml-auto inline-flex h-8 items-center rounded-md border px-3 text-sm">
                {"\u663e\u793a\u5217"} <ChevronDown className="ml-2 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.columnDef.header?.toString() || column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {onReorder ? (
        <DndContext
          sensors={sortableSensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleSortableDragStart}
          onDragCancel={clearSortableDrag}
          onDragEnd={(event) => {
            clearSortableDrag()
            handleSortableDragEnd(event)
          }}
        >
          <div className="relative w-full">
            <div
              ref={stickyWrapperRef}
              className={tableWrapperClass}
              style={stickyWrapperStyle}
            >
              <Table
                className={tableClass}
                style={tableLayoutStyle}
                disableContainerScroll
              >
                <TableHeader className="[&_tr]:border-b-0">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const meta = readColumnMeta(
                          header.column.columnDef.meta,
                        )
                        const layout = applyPinLayout(
                          getCellLayoutProps(meta),
                          pinMap.get(header.column.id),
                          "head",
                          stickyMode,
                        )
                        return (
                          <TableHead
                            key={`${header.id}:${getColumnSortDirection(header.column.id, resolvedSorting) || "none"}`}
                            {...layout}
                          >
                            <DataTableCellContent noTruncate={meta?.noTruncate}>
                              {renderDataTableHeaderCell(
                                header,
                                allPageRowsSelected,
                                somePageRowsSelected,
                                selectAllDisabled,
                                handleToggleAllPageRows,
                                resolvedSorting,
                              )}
                            </DataTableCellContent>
                          </TableHead>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <SortableContext
                  items={sortableRowIds}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {tableBodyContent}
                    <EmptyPadRows count={emptyPadCount} colSpan={columns.length} />
                  </TableBody>
                </SortableContext>
              </Table>
            </div>
            <DataTableScrollJump viewportRef={stickyWrapperRef} />
          </div>
          <DragOverlay adjustScale={false}>
            {activeDragRow && activeDragLayout ? (
              <table
                data-slot="table-drag-overlay"
                aria-hidden="true"
                inert
                className={cn("caption-bottom text-sm", tableClass)}
                style={{ width: activeDragLayout.width }}
              >
                <colgroup>
                  {activeDragRow.getVisibleCells().map((cell, index) => (
                    <col key={cell.column.id} style={{ width: activeDragLayout.cellWidths[index] }} />
                  ))}
                </colgroup>
                <tbody>
                  <tr
                    className={cn(
                      ROW_HEIGHT_CLASS,
                      "outline outline-border bg-background shadow-md",
                    )}
                    style={{ height: activeDragLayout.height }}
                  >
                    {activeDragRow.getVisibleCells().map(cell =>
                      renderDataTableBodyCell(
                        activeDragRow,
                        cell,
                        activeDragRow.getIsSelected(),
                        activeDragRow.getIsSelected() || canSelectMoreRows,
                        bodyCellOptions,
                      ),
                    )}
                  </tr>
                </tbody>
              </table>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <div className="relative w-full">
          <div
            ref={stickyWrapperRef}
            className={tableWrapperClass}
            style={stickyWrapperStyle}
          >
            <Table
              className={tableClass}
              style={tableLayoutStyle}
              disableContainerScroll
            >
              <TableHeader className="[&_tr]:border-b-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const meta = readColumnMeta(header.column.columnDef.meta)
                      const layout = applyPinLayout(
                        getCellLayoutProps(meta),
                        pinMap.get(header.column.id),
                        "head",
                        stickyMode,
                      )
                      return (
                        <TableHead
                          key={`${header.id}:${getColumnSortDirection(header.column.id, resolvedSorting) || "none"}`}
                          {...layout}
                        >
                          <DataTableCellContent noTruncate={meta?.noTruncate}>
                            {renderDataTableHeaderCell(
                              header,
                              allPageRowsSelected,
                              somePageRowsSelected,
                              selectAllDisabled,
                              handleToggleAllPageRows,
                              resolvedSorting,
                            )}
                          </DataTableCellContent>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {tableBodyContent}
                <EmptyPadRows count={emptyPadCount} colSpan={columns.length} />
              </TableBody>
            </Table>
          </div>
          <DataTableScrollJump viewportRef={stickyWrapperRef} />
        </div>
      )}
      {showFooterRow && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2",

            frameless ? "px-3 py-2.5" : "py-4 pl-2",
          )}
        >
          <DataTableRowActions
            selectedCount={selectedCount}
            selectedRows={selectedRows}
            actionLabel={selectionActionLabel}
            actionLoading={selectionActionLoading}
            onAction={onSelectionAction}
            actions={selectionActions}
            onClearSelection={() => setRowSelection({})}
          />
          {showPaginationControls && (
            <DataTablePagination
              pageIndex={pagination.pageIndex}
              pageCount={pageCount}
              pageSize={pagination.pageSize}
              filteredTotal={filteredTotal}
              pageSizeOptions={pageSizeOptions}
              showPageSizeSelector={showPageSizeSelector}
              showPageJumpInput={showPageJumpInput}
              onPageIndexChange={(pageIndex) => {
                handlePaginationChange((current) => ({
                  ...current,
                  pageIndex,
                }))
              }}
              onPageSizeChange={(nextPageSize) => {
                handlePaginationChange((current) => ({
                  ...current,
                  pageIndex: 0,
                  pageSize: nextPageSize,
                }))
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

function MemoDataTableRow<TData>({
  row,
  isSelected,
  canSelect,
  bodyCellOptions,
  pinMap,
  stickyMode = false,
}: {
  row: Row<TData>
  isSelected: boolean
  canSelect: boolean
  bodyCellOptions?: {
    serialOrder?: AdminTableSerialOrder
    rowsOnPage: number
    coreRowIndexById?: Map<string, number>
  }
  pinMap?: Map<string, DataTablePinInfo>
  stickyMode?: boolean
}) {
  return (
    <TableRow
      className={ROW_HEIGHT_CLASS}
      data-state={isSelected && "selected"}
    >
      {row.getVisibleCells().map((cell) =>
        renderDataTableBodyCell(
          row,
          cell,
          isSelected,
          canSelect,
          bodyCellOptions,
          pinMap,
          stickyMode,
        ),
      )}
    </TableRow>
  )
}

const MemoizedDataTableRow = React.memo(
  MemoDataTableRow,
  (previous, next) =>
    previous.row.id === next.row.id &&
    previous.row.index === next.row.index &&
    previous.isSelected === next.isSelected &&

    previous.canSelect === next.canSelect &&
    previous.row.original === next.row.original &&
    previous.stickyMode === next.stickyMode &&
    previous.pinMap === next.pinMap &&
    previous.bodyCellOptions?.rowsOnPage === next.bodyCellOptions?.rowsOnPage &&
    previous.bodyCellOptions?.serialOrder === next.bodyCellOptions?.serialOrder &&
    previous.bodyCellOptions?.coreRowIndexById?.get(previous.row.id) ===
      next.bodyCellOptions?.coreRowIndexById?.get(next.row.id),
) as typeof MemoDataTableRow

function SortableDataTableRow({
  id,
  className,
  children,
}: {
  id: string
  className?: string
  children: React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  }

  return (
    <SortableRowHandleProvider
      value={{
        setActivatorNodeRef,
        listeners,
        attributes,
        isDragging,
      }}
    >
      <TableRow
        ref={setNodeRef}
        data-sortable-row-id={id}
        style={style}
        className={cn(className, isDragging && "bg-muted/40")}
        data-dragging={isDragging || undefined}
      >
        {children}
      </TableRow>
    </SortableRowHandleProvider>
  )
}
